import {
	ActionMap,
	FormContext_Action_PayloadMap,
	FormContext_Actions,
	formContext_reducer,
	isFormContext_Action_PayloadMap,
} from '@/components/forms/FormContext';
import { ReplaceVirtuallyFormState, StepMap } from './constants';
import { match } from '@/helpers/match';
import logError from '@/helpers/logError';
import {
	AddressStepActionMap,
	AddressStepActions,
	addressStepReducer,
	isAddressStepActionMap,
} from './steps/AddressStep/state';
import {
	ContactInfoStepActionMap,
	ContactInfoStepActions,
	contactInfoStepReducer,
	isContactInfoStepActionMap,
} from './steps/ContactInfoStep/state';
import {
	isSystemIssuesStepActionMap,
	SystemIssuesStepActionMap,
	SystemIssuesStepActions,
	systemIssuesStepReducer,
} from './steps/SystemIssuesStep/state';
import {
	isPictureStepActionMap,
	PictureStepActionMap,
	PictureStepActions,
	pictureStepReducer,
} from './steps/PictureStep/state';
import userProfile from '@/helpers/userProfile';
import {
	isMessageStepActionMap,
	MessageStepActionMap,
	MessageStepActions,
	messageStepReducer,
} from './steps/MessageStep/state';
import {
	isSystemAgeStepActionMap,
	SystemAgeStepActionMap,
	SystemAgeStepActions,
	systemAgeStepReducer,
} from './steps/SystemAgeStep/state';
import {
	isBuyingDecisionStepActionMap,
	BuyingDecisionStepActionMap,
	BuyingDecisionStepActions,
	buyingDecisionStepReducer,
} from './steps/BuyingDecisionStep/state';
import {
	isUrgencyStepActionMap,
	UrgencyStepActionMap,
	UrgencyStepActions,
	urgencyStepReducer,
} from './steps/UrgencyStep/state';

/**
 * These are not step specific actions, they are actions that can be handled by
 * the form context
 */
export const ReplaceVirtuallyFormActions = {
	SET_HUBSPOT_DEAL_ID: 'SET_HUBSPOT_DEAL_ID',
	HYDRATE_STATE: 'HYDRATE_STATE',
} as const;

/**
 * The payloads for the non-step specific actions
 */
export interface ReplaceVirtuallyFormActionPayload {
	[ReplaceVirtuallyFormActions.SET_HUBSPOT_DEAL_ID]: { hubspotDealId: string };
	[ReplaceVirtuallyFormActions.HYDRATE_STATE]: ReplaceVirtuallyFormState;
}

/**
 * The map for what payload corresponds to what action also, not step specific
 */
export type ReplaceVirtuallyFormNoStepActionMap =
	ActionMap<ReplaceVirtuallyFormActionPayload>[keyof ActionMap<ReplaceVirtuallyFormActionPayload>];

/**
 * The map for all the actions that can be handled by the reducer. If you add a
 * new step, you will need to add the action map for that step here.
 */
export type ReplaceVirtuallyFormActionMap =
	| FormContext_Action_PayloadMap<StepMap>
	| UrgencyStepActionMap
	| AddressStepActionMap
	| ContactInfoStepActionMap
	| SystemIssuesStepActionMap
	| PictureStepActionMap
	| BuyingDecisionStepActionMap
	| MessageStepActionMap
	| SystemAgeStepActionMap
	| ReplaceVirtuallyFormNoStepActionMap;
/**
 * The rootReducer for the form. This is where all the actions are handled. FYI
 * order does not matter.
 *
 * If you add a new step, you will need to add the reducer for that step here.
 */
export const rootReducer = (
	state: ReplaceVirtuallyFormState,
	action: ReplaceVirtuallyFormActionMap,
): ReplaceVirtuallyFormState => {
	const [, updateProfile] = userProfile();
	const updatedState = match<ReplaceVirtuallyFormState>(
		[
			// This is the only action the form context will handle
			action.type in FormContext_Actions,
			(): ReplaceVirtuallyFormState => {
				if (!isFormContext_Action_PayloadMap<StepMap>(action)) return state;
				return formContext_reducer<StepMap, ReplaceVirtuallyFormState>(
					state,
					action,
				);
			},
		],
		[
			action.type in UrgencyStepActions,
			(): ReplaceVirtuallyFormState => {
				if (!isUrgencyStepActionMap(action)) return state;
				return urgencyStepReducer(state, action);
			},
		],
		[
			action.type in AddressStepActions,
			(): ReplaceVirtuallyFormState => {
				if (!isAddressStepActionMap(action)) return state;
				return addressStepReducer(state, action);
			},
		],
		[
			action.type in ContactInfoStepActions,
			(): ReplaceVirtuallyFormState => {
				if (!isContactInfoStepActionMap(action)) return state;
				return contactInfoStepReducer(state, action);
			},
		],
		[
			action.type in SystemIssuesStepActions,
			(): ReplaceVirtuallyFormState => {
				if (!isSystemIssuesStepActionMap(action)) return state;
				return systemIssuesStepReducer(state, action);
			},
		],
		[
			action.type in PictureStepActions,
			(): ReplaceVirtuallyFormState => {
				if (!isPictureStepActionMap(action)) return state;
				return pictureStepReducer(state, action);
			},
		],
		[
			action.type in BuyingDecisionStepActions,
			(): ReplaceVirtuallyFormState => {
				if (!isBuyingDecisionStepActionMap(action)) return state;
				return buyingDecisionStepReducer(state, action);
			},
		],
		[
			action.type in MessageStepActions,
			(): ReplaceVirtuallyFormState => {
				if (!isMessageStepActionMap(action)) return state;
				return messageStepReducer(state, action);
			},
		],
		[
			action.type in SystemAgeStepActions,
			(): ReplaceVirtuallyFormState => {
				if (!isSystemAgeStepActionMap(action)) return state;
				return systemAgeStepReducer(state, action);
			},
		],
		[
			action.type in ReplaceVirtuallyFormActions,
			(): ReplaceVirtuallyFormState => {
				switch (action.type) {
					case ReplaceVirtuallyFormActions.SET_HUBSPOT_DEAL_ID:
						updateProfile('dealId', action.payload.hubspotDealId);
						return {
							...state,
							hubspotDealId: action.payload.hubspotDealId,
						};
					case ReplaceVirtuallyFormActions.HYDRATE_STATE:
						return {
							...state,
							...action.payload,
							/**
							 * We don't want to hydrate the steps because they have functions, which are not serializable
							 * so we don't want to overwrite them with the ones pulled from the server because the server
							 * only contains serializable data.
							 */
							steps: state.steps,
						};
					default:
						return state;
				}
			},
		],
	);
	if (!updatedState)
		throw logError(new Error('rootReducer failed to return a state'));
	return updatedState;
};
